import { addDecision, editDecision } from "../../../api/decision/decision";
import { AddUpperDecision } from ".";
import { Button } from "../../Button";
import { CloseBtn } from "../../CloseBtn";
import { Col, Row } from "react-grid-system";
import { DatePicker } from "../../DatePicker";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { getCategoryList } from "../../../api/decision/category";
import { getDecisionUnitList, createOrganizationUnit } from "../../../api/organization-unit/organization-unit";
import { getLabelList } from "../../../api/decision/label";
import { Keywords } from "../../Keywords";
import { Link } from "react-router-dom";
import { Loader } from "../../Loader";
import { Select } from "../../Select";
import { StyledDecisionActions } from "../../../styles/StyledDecision";
import { StyledField } from "../../../styles/StyledField";
import { StyledTallModal } from "../../../styles/StyledModal";
import { StyledModalFooterActions } from "../../../styles/StyledAddUpperDecision";
import { useDecisionContext } from "../../../context/decisionContext";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useRef } from "react";
import CloseIcon from "../../../assets/img/icons/close.svg";
import EditIcon from "../../../assets/img/icons/edit.svg";
import Modal from "react-modal";
import ModalHeader from "../../ModalHeader/ModalHeader";
import moment from "moment";
import PropTypes from "prop-types";
import SaveIcon from "../../../assets/img/icons/save.svg";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { toast } from "react-toastify";

// Validation schema for decision form
const validationSchema = Yup.object().shape({
	name: Yup.string()
		.required("Name is required")
		.min(10, "Name must be at least 10 characters")
		.max(200, "Name must not exceed 200 characters"),
	referenceNumber: Yup.string()
		.nullable()
		.notRequired(), // Optional field
	decisionDate: Yup.date()
		.nullable()
		.required("Decision date is required"),
	unit: Yup.string()
		.required("Unit is required"),
	category: Yup.string()
		.required("Category is required"),
	label: Yup.string()
		.required("Label is required"),
	validFrom: Yup.date()
		.nullable()
		.required("Valid from date is required"),
	validTo: Yup.date()
		.nullable()
		.required("Valid to date is required"),
	matter: Yup.string()
		.nullable()
		.notRequired(), // Optional field
	keywords: Yup.array()
		.nullable()
		.notRequired() // Optional field
});

export default function DecisionHeaderEdit({ edit, newDecision, disableEditingMode, refetch, prefillData }) {
	//CONTEXT
	const { id, name, referenceNumber, decisionDate, validFrom, validTo, category, label, unit, matter, upperDecision, keywords } =
		useDecisionContext();
	const navigate = useNavigate();
	const { axiosPrivate } = useAxiosPrivate();
	const queryClient = useQueryClient();

	//QUERIES
	const { data: categoryList, isLoading: isLoadingCategoryList } = useQuery("categoryList", () => getCategoryList(axiosPrivate));
	const { data: labelList, isLoading: isLoadingLabelList } = useQuery("labelList", () => getLabelList(axiosPrivate));
	const { data: unitList, isLoading: isLoadingUnitList } = useQuery("unitList", () => getDecisionUnitList(axiosPrivate));

	//STATES
	const [modalIsOpen, setIsOpen] = useState(false);
	const [unitData, setUnitData] = useState(null);
	const [selectedUpperDecision, setSelectedUpperDecision] = useState(upperDecision);
	const [matters, setMatters] = useState(null);
	const [currentUserId, setCurrentUserId] = useState(null);
	const [showUnitCreationDialog, setShowUnitCreationDialog] = useState(false);
	const { can } = useCurrentUser();
	const [unknownUnitName, setUnknownUnitName] = useState("");

	const matterRef = useRef();
	const formikRef = useRef();

	// Selects a unit from code (document prefill, newly created unit). Sets the form field
	// directly: putting it into initialValues would re-initialise the form and wipe other input.
	const selectUnit = (selectedUnit) => {
		setUnitData(selectedUnit);
		formikRef.current?.setFieldValue("unit", selectedUnit.value);
	};
	useEffect(() => {
		const fetchUser = async () => {
			try {
				const res = await axiosPrivate.get("/user/profile.json");
				setCurrentUserId(res.data.id);
			} catch (err) {
				console.error("Failed to fetch user profile", err);
			}
		};
		fetchUser();
	}, []);

	useEffect(() => {
		if (unit) {
			setUnitData({ value: unit.id, label: unit.name });
		}
	}, [unit]);

	useEffect(() => {
		if (unitList) {
			if (unit) {
				unitList.map((el) => {
					if (el.id === unit.id) {
						setMatters(el.matters);
					}
				});
			} else {
				setMatters(null);
			}
		}
	}, [isLoadingUnitList]);

	useEffect(() => {
		if (matterRef.current) {
			if (matterRef.current.props.value) {
				matterRef.current.props.value.value = null;
				matterRef.current.props.value.label = "";
			}
		}
	}, [matters]);

	useEffect(() => {
		Modal.setAppElement("body");

		// Enhanced prefill data processing with improved unit matching
		if (prefillData) {
			// Try to match extracted Swedish unit to existing units with fuzzy matching
			if (prefillData.unit && unitList && unitList.length > 0) {
				const extractedUnit = prefillData.unit.toLowerCase();

				// Try exact match first
				let matchedUnit = unitList.find(unit =>
					unit.name.toLowerCase() === extractedUnit
				);

				// Try partial matches if no exact match
				if (!matchedUnit) {
					matchedUnit = unitList.find(unit =>
						unit.name.toLowerCase().includes(extractedUnit) ||
						extractedUnit.includes(unit.name.toLowerCase())
					);
				}

				// Try keyword matching for Swedish organizational terms
				if (!matchedUnit) {
					const unitKeywords = extractedUnit.split(/[\s,]+/).filter(word => word.length > 3);
					matchedUnit = unitList.find(unit =>
						unitKeywords.some(keyword =>
							unit.name.toLowerCase().includes(keyword) ||
							keyword.includes(unit.name.toLowerCase())
						)
					);
				}

				if (matchedUnit) {
					console.log(`Matched extracted unit "${prefillData.unit}" to system unit "${matchedUnit.name}"`);
					selectUnit({ value: matchedUnit.id, label: matchedUnit.name });
					setMatters(matchedUnit.matters);
				} else {
					console.log(`Could not match extracted unit "${prefillData.unit}" to any system unit`);
					// Show unit creation dialog for unknown unit (only organization admins can create units)
					if (can("createUnits")) {
						setUnknownUnitName(prefillData.unit);
						setShowUnitCreationDialog(true);
					}
				}
			}

			// Note: Don't clear prefillDecisionData here - it will be cleared after decision creation
			sessionStorage.removeItem("documentToastShown");
		}
	}, [prefillData, unitList]);

	const handleCreateUnit = async () => {
		try {
			// The creating organization admin becomes the unit admin, so they can create the decision in it
			const result = await createOrganizationUnit(axiosPrivate, unknownUnitName, currentUserId);
			if (result && result.id) {
				// Successfully created unit
				toast.success(`Unit "${unknownUnitName}" added`);
				
				// Set the newly created unit as selected
				selectUnit({ value: result.id, label: result.name });
				
				// Update the query cache to include the new unit
				queryClient.setQueryData("unitList", (oldData) => {
					if (oldData) {
						return [...oldData, {
							id: result.id,
							name: result.name,
							matters: []
						}];
					}
					return oldData;
				});

				// Invalidate and refetch the unit list query to ensure fresh data
				queryClient.invalidateQueries("unitList");

				// Close the dialog
				setShowUnitCreationDialog(false);

				// Clear the unknown unit name
				setUnknownUnitName("");
			} else {
				throw new Error("Failed to create organization unit");
			}
		} catch (error) {
			console.error("Error creating organization unit:", error);
			toast.error(`Could not create unit "${unknownUnitName}". Please try again.`);
		}
	};

	// Helper function to suggest category based on document content
	const suggestCategoryFromContent = (prefillData, categoryList) => {
		if (!prefillData || !categoryList) return "";

		const content = (prefillData.title + " " + prefillData.description + " " + prefillData.keywords).toLowerCase();

		// Category mapping based on Swedish administrative terms
		const categoryMappings = {
			"personal": ["personal", "anställd", "medarbetare", "rekrytering"],
			"budget": ["budget", "ekonomi", "finansiell", "kostnad"],
			"education": ["utbildning", "kurs", "student", "undervisning", "studieordning"],
			"research": ["forskning", "projekt", "doktorand"],
			"policy": ["policy", "riktlinjer", "styrdokument", "handläggningsordning", "disciplin"],
			"administration": ["administration", "förvaltning", "organisation"]
		};

		for (const [categoryKey, terms] of Object.entries(categoryMappings)) {
			if (terms.some(term => content.includes(term))) {
				const matchedCategory = categoryList.find(cat =>
					cat.name.toLowerCase().includes(categoryKey) ||
					categoryKey.includes(cat.name.toLowerCase())
				);
				if (matchedCategory) return matchedCategory.id;
			}
		}

		return "";
	};

	// Helper function to parse Swedish validity terms
	// Normalize Swedish date formats to ISO format
	const normalizeSwedishDate = (dateStr) => {
		if (!dateStr || typeof dateStr !== "string") return null;

		// Handle YY-MM-DD format (Swedish short format)
		const shortDateMatch = dateStr.match(/^(\d{2})-(\d{2})-(\d{2})$/);
		if (shortDateMatch) {
			const [, year, month, day] = shortDateMatch;
			// Assume 20XX for years 00-29, 19XX for years 30-99
			const fullYear = parseInt(year) <= 29 ? `20${year}` : `19${year}`;
			return `${fullYear}-${month}-${day}`;
		}

		// Handle YYYY-MM-DD format (already correct)
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
			return dateStr;
		}

		// Handle ISO date-time from the API (e.g. 2026-03-15T00:00:00+00:00).
		// Keep the calendar date as stored instead of converting time zones.
		const isoDateTimeMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})T/);
		if (isoDateTimeMatch) {
			return isoDateTimeMatch[1];
		}
		
		// Return null for unrecognized formats
		return null;
	};

	const parseValidityPeriod = (validityText, decisionDate) => {
		console.log("Parsing validity period:", validityText, "with decision date:", decisionDate);

		if (!validityText) return { validFrom: null, validTo: null };

		const validity = validityText.toLowerCase().trim();
		const normalizedDate = normalizeSwedishDate(decisionDate);
		const baseDate = normalizedDate ? moment(normalizedDate) : moment();

		// Handle "tillsvidare" (indefinite validity)
		if (validity.includes("tillsvidare") || validity.includes("tills vidare")) {
			console.log("Found 'tillsvidare' - setting indefinite validity");
			return {
				validFrom: baseDate.isValid() ? baseDate.format("YYYY-MM-DD") : null,
				validTo: null // Indefinite validity - no end date
			};
		}

		// Handle specific date ranges or periods
		// Add more parsing logic for other Swedish validity terms if needed

		return { validFrom: null, validTo: null };
	};

	const validityDates = prefillData ? parseValidityPeriod(prefillData.validityPeriod, prefillData.date) : {};

	console.log("🔍 DecisionHeaderEdit: prefillData received:", prefillData);
	console.log("🔍 DecisionHeaderEdit: Prefill data in form:", prefillData);
	console.log("🔍 DecisionHeaderEdit: Validity dates computed:", validityDates);

	const initialValues = {
		name: prefillData?.title || name || "",
		referenceNumber: prefillData?.referenceNumber || referenceNumber || "",
		decisionDate: (() => {
			const dateToUse = prefillData?.date || decisionDate;
			console.log("🔍 DecisionHeaderEdit: decisionDate dateToUse:", dateToUse);
			if (!dateToUse) return "";
			const normalizedDate = normalizeSwedishDate(dateToUse);
			return normalizedDate ? moment(normalizedDate).format("YYYY-MM-DD") : "";
		})(),
		// Show prefilled values but allow user to change them
		validFrom: (() => {
			// If we have validity dates from prefill data, use those
			if (validityDates?.validFrom) return validityDates.validFrom;
			// Otherwise use existing validFrom if available
			if (!validFrom) return "";
			const normalizedFrom = normalizeSwedishDate(validFrom);
			return normalizedFrom && moment(normalizedFrom).isValid() ? moment(normalizedFrom).format("YYYY-MM-DD") : "";
		})(),
		validTo: (() => {
			// If we have validity dates from prefill data, use those  
			if (validityDates?.validTo) return validityDates.validTo;
			// Otherwise use existing validTo if available
			if (!validTo) return "";
			const normalizedTo = normalizeSwedishDate(validTo);
			return normalizedTo && moment(normalizedTo).isValid() ? moment(normalizedTo).format("YYYY-MM-DD") : "";
		})(),
		category: category ? category.id : "",
		label: label ? label.id : "",
		unit: unit ? unit.id : "",
		matter: matter ? matter.id : "",
		keywords: (prefillData?.keywords ? prefillData.keywords.split(",").map(k => k.trim()).filter(k => k) : []) || keywords || [],
	};

	console.log("🔍 DecisionHeaderEdit: Final initialValues computed:", {
		name: initialValues.name,
		referenceNumber: initialValues.referenceNumber,
		decisionDate: initialValues.decisionDate,
		validFrom: initialValues.validFrom,
		validTo: initialValues.validTo,
		unit: initialValues.unit,
		keywords: initialValues.keywords
	});

	console.log("Initial form values:", initialValues);

	const submitHandler = (values) => {
		console.log("Form submitted with values:", values);
		console.log("New decision:", newDecision);
		console.log("Current user ID:", currentUserId);
		console.log("Expected validFrom from validity dates:", validityDates.validFrom);
		console.log("Initial form validFrom:", initialValues.validFrom);

		if (newDecision) {
			// Clean up the values - remove empty strings and null values for entity fields
			const cleanedValues = { ...values };

			// Ensure required fields have default values
			if (!cleanedValues.label || cleanedValues.label === "") {
				// Set default label to the first available label if exists
				cleanedValues.label = (labelList && labelList.length > 0) ? labelList[0].id : 1;
			}

			// Remove empty unit field if present
			if (!cleanedValues.unit || cleanedValues.unit === "") {
				delete cleanedValues.unit;
			}

			// Ensure category has a default value
			if (!cleanedValues.category || cleanedValues.category === "") {
				// Set default category to the first available category if exists
				cleanedValues.category = (categoryList && categoryList.length > 0) ? categoryList[0].id : 1;
			}

			// Remove empty matter field if present
			if (!cleanedValues.matter || cleanedValues.matter === "") {
				delete cleanedValues.matter;
			}

			// Limit keywords to maximum 2 elements (API constraint)
			if (cleanedValues.keywords && Array.isArray(cleanedValues.keywords) && cleanedValues.keywords.length > 2) {
				cleanedValues.keywords = cleanedValues.keywords.slice(0, 2);
				console.log("Limited keywords to first 2 elements:", cleanedValues.keywords);
			}

			// Remove null date fields - backend expects them to be omitted if not set
			// Only remove date fields if they are explicitly null, empty string, or undefined
			if (!cleanedValues.validFrom || cleanedValues.validFrom === "" || cleanedValues.validFrom === null || cleanedValues.validFrom === undefined) {
				delete cleanedValues.validFrom;
			}

			// Ensure validTo has a value - API requires it
			if (!cleanedValues.validTo || cleanedValues.validTo === "" || cleanedValues.validTo === null || cleanedValues.validTo === undefined) {
				// For indefinite validity (Tillsvidare), set a far future date
				cleanedValues.validTo = "2099-12-31";
			}

			if (!cleanedValues.decisionDate || cleanedValues.decisionDate === "" || cleanedValues.decisionDate === null || cleanedValues.decisionDate === undefined) {
				delete cleanedValues.decisionDate;
			}

			// Special validation: if validFrom is missing but we parsed validity period, ensure it's included
			if (!cleanedValues.validFrom && validityDates.validFrom) {
				cleanedValues.validFrom = validityDates.validFrom;
				console.log("Adding missing validFrom from parsed validity period:", cleanedValues.validFrom);
			}

			console.log("Cleaned values before submission:", cleanedValues);

			// Final validation: ensure we have the validFrom date if it was parsed from validity period
			// This handles cases where the form field might not have the value due to re-rendering issues
			if (!cleanedValues.validFrom && validityDates.validFrom) {
				console.log("Form missing validFrom, adding from parsed validity:", validityDates.validFrom);
				cleanedValues.validFrom = validityDates.validFrom;
			}

			// New decisions start as a Draft, owned by the user who creates them
			const decisionData = selectedUpperDecision
				? { ...cleanedValues, upperDecision: selectedUpperDecision.id }
				: { ...cleanedValues };

			console.log("Decision data being sent:", decisionData);

			// Add enhanced error handling to see what's causing the 400 error
			addDecision(axiosPrivate, navigate, decisionData)
				.then((response) => {
					console.log("Decision creation response:", response);
				})
				.catch((error) => {
					console.error("Decision creation failed:", error);
					if (error?.response?.status === 400) {
						console.error("400 Error Details:", {
							status: error.response.status,
							statusText: error.response.statusText,
							data: error.response.data,
							headers: error.response.headers
						});
					}
				});
		} else {
			editDecision(
				axiosPrivate,
				() => {
					disableEditingMode();
					refetch();
				},
				id,
				selectedUpperDecision ? { ...values, upperDecision: selectedUpperDecision.id } : { ...values, upperDecision: null }
			);
		}
	};

	return (
		<>
			<Formik 
				innerRef={formikRef}
				initialValues={initialValues} 
				enableReinitialize={true} // Enable reinitialize to update when prefillData arrives
				validationSchema={validationSchema}
				onSubmit={(values, { setSubmitting }) => {
					// Check for validation errors before submitting
					validationSchema.validate(values).then(() => {
						submitHandler(values);
						setSubmitting(false);
					}).catch((error) => {
						toast.error(`Validation error: ${error.message}`);
						setSubmitting(false);
					});
				}}
			>
				{({ values, setFieldValue, errors, touched, isSubmitting, validateForm }) => (
					<Form>
						<Row>
							<Col sm={5}>
								<StyledField edit>
									<label>
										Name
									</label>
									<Field as="textarea" id="name" name="name" data-testid="name" />
									{errors.name && touched.name && (
										<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
											{errors.name}
										</div>
									)}
								</StyledField>
								{unitData && (
									<StyledField>
										<label>
											Upper decision
											{!selectedUpperDecision && (
												<Button
													bg={false}
													small={true}
													icon={EditIcon}
													initial
													onClick={!isLoadingUnitList ? () => setIsOpen(true) : () => { }}
												>
													Add upper decision
												</Button>
											)}
										</label>
										{selectedUpperDecision && (
											<StyledField>
												<div className="wrapper">
													<span>{selectedUpperDecision.name}</span>
													<CloseBtn onClick={() => setSelectedUpperDecision(null)} icon={CloseIcon} small />
												</div>
											</StyledField>
										)}
									</StyledField>
								)}
							</Col>
							<Col sm={6} offset={{ sm: 1 }}>
								<Row>
									<Col sm={4} xl={3} xxxl={2}>
										<StyledField mb="4px" edit>
											<label>
												Reference number (optional)
											</label>
											<Field id="referenceNumber" name="referenceNumber" data-testid="referenceNumber" />
											{errors.referenceNumber && touched.referenceNumber && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.referenceNumber}
												</div>
											)}
										</StyledField>
									</Col>
									<Col sm={4} xl={3} xxxl={2}>
										<StyledField mb="12px" edit>
											<label>Category</label>
											{isLoadingCategoryList ? (
												<>
													<Loader />
													<input type="text" />
												</>
											) : (
												<Select
													options={(categoryList || [])
														.map((el) => ({
															value: el.id,
															label: el.name,
														}))}
													value={values.category}
													onChange={setFieldValue}
													id="category"
													name="category"
													data-testid="category"
												/>
											)}
											{errors.category && touched.category && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.category}
												</div>
											)}
										</StyledField>
									</Col>
									<Col sm={4} xl={3} xxxl={2}>
										<StyledField mb="12px" edit>
											<label>Label</label>
											{isLoadingLabelList ? (
												<>
													<Loader />
													<input type="text" />
												</>
											) : (
												<Select
													options={(labelList || [])
														.map((el) => ({
															value: el.id,
															label: el.name,
														}))}
													value={values.label}
													onChange={setFieldValue}
													id="label"
													name="label"
													data-testid="label"
												/>
											)}
											{errors.label && touched.label && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.label}
												</div>
											)}
										</StyledField>
									</Col>
								</Row>
								<Row>
									<Col>
										<StyledField mb="12px" edit>
											<label>Unit</label>
											{isLoadingUnitList ? (
												<>
													<Loader />
													<input type="text" />
												</>
											) : (
												<Select
													options={unitList.map((el) => {
														return { value: el.id, label: el.name, matters: el.matters };
													})}
													value={values.unit}
													onChange={setFieldValue}
													setUnitData={setUnitData}
													setMatters={setMatters}
													id="unit"
													name="unit"
													data-testid="unit"
												/>
											)}
											{errors.unit && touched.unit && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.unit}
												</div>
											)}
										</StyledField>
									</Col>
								</Row>
								{matters && (
									<Row>
										<Col>
											<StyledField mb="12px" edit>
												<label>Matter</label>
												{isLoadingUnitList ? (
													<>
														<Loader />
														<input type="text" />
													</>
												) : (
													<Select
														options={matters.map((matter) => {
															return { value: matter.id, label: matter.name };
														})}
														value={values.matter}
														onChange={setFieldValue}
														id="matter"
														name="matter"
														data-testid="matter"
														ref={matterRef}
													/>
												)}
											</StyledField>
										</Col>
									</Row>
								)}
								<Row>
									<Col sm={4} xl={3} xxxl={2}>
										<StyledField edit>
											<label>
												Decision date
											</label>
											<DatePicker id="decisionDate" name="decisionDate" data-testid="decisionDate" autoComplete="off" />
											{errors.decisionDate && touched.decisionDate && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.decisionDate}
												</div>
											)}
										</StyledField>
									</Col>
									<Col sm={4} xl={3} xxxl={2}>
										<StyledField edit>
											<label>
												Valid from
											</label>
											<DatePicker id="validFrom" name="validFrom" data-testid="validFrom" autoComplete="off" />
											{errors.validFrom && touched.validFrom && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.validFrom}
												</div>
											)}
										</StyledField>
									</Col>
									<Col sm={4} xl={3} xxxl={2}>
										<StyledField edit>
											<label>
												Valid to
											</label>
											<DatePicker id="validTo" name="validTo" data-testid="validTo" autoComplete="off" />
											{errors.validTo && touched.validTo && (
												<div style={{ color: "#ef4444", fontSize: "0.875rem", marginTop: "0.25rem" }}>
													{errors.validTo}
												</div>
											)}
										</StyledField>
									</Col>
									<Col sm={12} xxxl={6}>
										<StyledField>
											<Keywords
												id="keywords"
												name="keywords"
												keywords={keywords}
												setFieldValue={setFieldValue}
												edit
												data-testid="keywords"
											/>
										</StyledField>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row>
							<Col sm={5}>
								<StyledDecisionActions>
									{newDecision && <Link to="/">Cancel</Link>}
									{edit && (
										<Button onClick={disableEditingMode} bg={false} small={true}>
											Cancel
										</Button>
									)}
									<Button type="submit" icon={SaveIcon} disabled={isSubmitting}>
										{isSubmitting ? "Processing..." : (newDecision ? "Create decision" : "Save changes")}
									</Button>
								</StyledDecisionActions>
							</Col>
						</Row>
					</Form>
				)}
			</Formik>
			<Modal isOpen={modalIsOpen} onRequestClose={() => setIsOpen(false)} style={StyledTallModal} appElement={document.getElementById("app")}>
				<ModalHeader onClick={() => setIsOpen(false)}>Add upper decision</ModalHeader>
				<AddUpperDecision
					onClick={() => setIsOpen(false)}
					unit={unitData}
					matters={matters}
					selectedUpperDecision={setSelectedUpperDecision}
				/>
			</Modal>

			{/* Unit Creation Dialog */}
			<Modal
				isOpen={showUnitCreationDialog}
				onRequestClose={() => setShowUnitCreationDialog(false)}
				style={StyledTallModal}
				appElement={document.getElementById("app")}
			>
				<ModalHeader onClick={() => setShowUnitCreationDialog(false)}>
					Unknown unit
				</ModalHeader>
				<>
					<p>The extracted unit <strong>"{unknownUnitName}"</strong> was not found in the system.</p>
					<p>Would you like to create a new unit with this name? You will be its unit admin.</p>
					
					<Row>
						<Col>
							<StyledModalFooterActions>
								<Button
									onClick={() => setShowUnitCreationDialog(false)}
									bg={false}
									small={true}
								>
									Cancel
								</Button>
								<Button
									onClick={handleCreateUnit}
								>
									Create unit
								</Button>
							</StyledModalFooterActions>
						</Col>
					</Row>
				</>
			</Modal>
		</>
	);
}

DecisionHeaderEdit.propTypes = {
	data: PropTypes.object,
	disableEditingMode: PropTypes.func,
	refetch: PropTypes.func,
	edit: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	newDecision: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	prefillData: PropTypes.object,
};
